import React, { useEffect, useState } from 'react';
import { Phone, Video, X } from 'lucide-react';

/* ============================================================
   ⚠️ CONFIG — Modifie ces valeurs pour tes propres liens
   ============================================================ */
// Ton lien de réunion permanente Google Meet (créé depuis meet.google.com)
const GOOGLE_MEET_URL = 'https://meet.google.com/new';
// Ton ID de room Zoom personnelle (ex: '1234567890') ou lien complet
const ZOOM_URL = 'https://zoom.us/start/videomeeting';
// Ton lien Microsoft Teams
const TEAMS_URL = 'https://teams.microsoft.com/l/meeting/new?subject=Réunion%20MCE';

export default function FloatingCall() {
  const [bottomOffset, setBottomOffset] = useState(145);
  const [isOpen, setIsOpen] = useState(false);

  // ============ POSITIONNEMENT DYNAMIQUE ============
  useEffect(() => {
    const updatePosition = () => {
      const launcher = document.querySelector('.mce-chat-launcher');
      let bottom = 145;

      if (launcher) {
        const rect = launcher.getBoundingClientRect();
        if (rect.height > 0 && rect.top > 0) {
          bottom = Math.max(145, Math.round(window.innerHeight - rect.top));
        }
      }

      setBottomOffset(Math.max(16, Math.min(bottom, window.innerHeight - 84)));
    };

    updatePosition();
    const frame = window.requestAnimationFrame(updatePosition);
    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition);
    window.addEventListener('load', updatePosition);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('load', updatePosition);
    };
  }, []);

  // ============ FERMETURE AU ESCAPE ============
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === 'Escape' && setIsOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // ============ OUVERTURE INTELLIGENTE (app native ou web) ============
  const openMeeting = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };

  return (
    <>
      {/* ============ BOUTON FLOTTANT ============ */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="global-call-button"
        style={{ bottom: `${bottomOffset}px`, display: 'none' }}
        aria-label="Démarrer un appel vidéo"
        title="Démarrer un appel vidéo (Meet, Zoom, Teams)"
      >
        <Video className="w-6 h-6" />
      </button>

      {/* ============ MODALE DE CHOIX ============ */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={() => setIsOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="call-modal-title"
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          {/* Contenu modale */}
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bouton fermer */}
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              aria-label="Fermer"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>

            {/* En-tête */}
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                <Phone className="w-7 h-7 text-blue-600" />
              </div>
              <h2 id="call-modal-title" className="text-xl font-black text-[#0F1E3D]">
                Démarrer un appel
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Choisissez votre plateforme préférée
              </p>
            </div>

            {/* Boutons plateformes */}
            <div className="space-y-3">
              {/* Google Meet */}
              <button
                type="button"
                onClick={() => openMeeting(GOOGLE_MEET_URL)}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-100 hover:border-blue-400 hover:bg-blue-50 transition-all group"
              >
                <div className="w-11 h-11 rounded-lg bg-white shadow-sm flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 87.5 72" className="w-7 h-7">
                    <path fill="#00832d" d="M49.5 36l8.5 9.7 11.4 7.3 2-16.9-2-16.6-11.6 6.4z"/>
                    <path fill="#0066da" d="M0 51.5V66c0 3.3 2.7 6 6 6h14.5l3-11-3-9.5-9.9-3z"/>
                    <path fill="#e94235" d="M20.5 0L0 20.5 10.6 24 20.5 21l3-9.5z"/>
                    <path fill="#2684fc" d="M20.5 20.5H0v31h20.5z"/>
                    <path fill="#00ac47" d="M82.6 8.7L69.5 19.5v33L82.7 63.2c2 1.5 4.8.1 4.8-2.4V11.1c0-2.5-2.9-4-4.9-2.4zM49.5 36v15.5h-29V72h43c3.3 0 6-2.7 6-6V52.7z"/>
                    <path fill="#ffba00" d="M63.5 0h-43v20.5h29V36l20-.1V6c0-3.3-2.7-6-6-6z"/>
                  </svg>
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold text-[14px] text-[#0F1E3D]">Google Meet</p>
                  <p className="text-[12px] text-gray-500">Lancer une réunion instantanée</p>
                </div>
              </button>

              {/* Zoom */}
              <button
                type="button"
                onClick={() => openMeeting(ZOOM_URL)}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-100 hover:border-blue-400 hover:bg-blue-50 transition-all group"
              >
                <div className="w-11 h-11 rounded-lg bg-[#2D8CFF] flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="white">
                    <path d="M2 8v8c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2zm18 1.5v5l4 2v-9l-4 2z"/>
                  </svg>
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold text-[14px] text-[#0F1E3D]">Zoom</p>
                  <p className="text-[12px] text-gray-500">Démarrer une réunion Zoom</p>
                </div>
              </button>

              {/* Microsoft Teams */}
              <button
                type="button"
                onClick={() => openMeeting(TEAMS_URL)}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-100 hover:border-blue-400 hover:bg-blue-50 transition-all group"
              >
                <div className="w-11 h-11 rounded-lg bg-[#4B53BC] flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="white">
                    <path d="M20.625 8.127c1.036 0 1.875-.84 1.875-1.877a1.876 1.876 0 1 0-1.875 1.877zm-3.75 1.5v6.75c0 2.485-2.016 4.5-4.5 4.5-2.052 0-3.774-1.374-4.319-3.25H4.5A1.5 1.5 0 0 1 3 16.127v-6.75a1.5 1.5 0 0 1 1.5-1.5h12.375zM10.5 10.877h-4.5v1.5h1.5v4.5h1.5v-4.5h1.5v-1.5zm10.125-1.5c-.827 0-1.5.673-1.5 1.5v5.25c0 .827.673 1.5 1.5 1.5s1.5-.673 1.5-1.5v-5.25c0-.827-.673-1.5-1.5-1.5z"/>
                  </svg>
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold text-[14px] text-[#0F1E3D]">Microsoft Teams</p>
                  <p className="text-[12px] text-gray-500">Démarrer une réunion Teams</p>
                </div>
              </button>
            </div>

            {/* Note */}
            <p className="text-[11px] text-gray-400 text-center mt-6 leading-relaxed">
              L'application s'ouvrira si elle est installée, sinon la version web sera utilisée.
            </p>
          </div>
        </div>
      )}
    </>
  );
}